import { useEffect, useState } from 'react';
import { countDefectsForUser, countPropertiesForUser } from '../db/repository';
import { deleteDb } from '../db/db';
import { deleteMyAccount, downloadMyData } from '../api/auth';
import type { Property, User } from '../db/types';

interface PropertyListProps {
  user: User;
  properties: Property[];
  onAdd: () => void;
  onOpen: (propertyId: string) => void;
  onSignUp: () => void;
  onAccountDeleted: () => void;
}

const PROPERTY_TYPE_LABELS: Record<Property['propertyType'], string> = {
  'HDB 3-room': 'HDB 3-room',
  'HDB 4-room': 'HDB 4-room',
  'HDB 5-room': 'HDB 5-room',
  Executive: 'Executive (HDB)',
  Condominium: 'Condominium',
  'Executive Condo': 'Executive Condo',
  Landed: 'Landed',
  Commercial: 'Commercial',
  Other: 'Other',
};

export function PropertyList({
  user,
  properties,
  onAdd,
  onOpen,
  onSignUp,
  onAccountDeleted,
}: PropertyListProps) {
  const [defectCount, setDefectCount] = useState(0);
  const [propertyCount, setPropertyCount] = useState(0);

  useEffect(() => {
    void countPropertiesForUser(user.id).then(setPropertyCount);
    void countDefectsForUser(user.id).then(setDefectCount);
  }, [user.id, properties.length]);

  if (properties.length === 0) {
    return (
      <section className="onboard" aria-labelledby="onboard-title">
        <p className="eyebrow">welcome</p>
        <h1 id="onboard-title" className="onboard-title">
          Start inspecting.
          <br />
          <span className="onboard-italic">No account needed.</span>
        </h1>
        <p className="onboard-lede">
          You can walk through your entire inspection — record every defect,
          generate the report — without creating an account. Your data stays on
          this device until you choose to save it.
        </p>
        <button type="button" className="btn-primary" onClick={onAdd}>
          Add your first property
        </button>
        <p className="onboard-meta">
          Built against BCA CONQUAS standards. No photos in v1 — coming later.
        </p>
      </section>
    );
  }

  return (
    <section aria-labelledby="props-title">
      <div className="page-head">
        <div>
          <p className="eyebrow">your inspections</p>
          <h1 id="props-title" className="page-title">
            {propertyCount} {propertyCount === 1 ? 'property' : 'properties'}
            {defectCount > 0 ? (
              <>
                <br />
                <span className="page-title-italic">
                  {defectCount} defect{defectCount === 1 ? '' : 's'} logged.
                </span>
              </>
            ) : null}
          </h1>
        </div>
      </div>

      <ul className="prop-list" role="list">
        {properties.map((p) => (
          <li key={p.id}>
            <button
              type="button"
              className="prop-card"
              onClick={() => onOpen(p.id)}
            >
              <div className="prop-card-main">
                <span className="prop-card-label">{p.label}</span>
                <span className="prop-card-type">
                  {PROPERTY_TYPE_LABELS[p.propertyType]}
                </span>
              </div>
              <span aria-hidden="true" className="prop-card-arrow">
                →
              </span>
            </button>
          </li>
        ))}
      </ul>

      <button type="button" className="btn-secondary btn-block" onClick={onAdd}>
        Add another property
      </button>

      {user.isAnonymous ? (
        <div className="save-cta">
          <p className="save-cta-lede">
            Your inspection lives only on this device right now. Save it to an
            email so you don't lose it if you clear browser data or change
            phones.
          </p>
          <button type="button" className="btn-primary" onClick={onSignUp}>
            Save this inspection
          </button>
        </div>
      ) : (
        <AccountPanel email={user.email} onDeleted={onAccountDeleted} />
      )}
    </section>
  );
}

function AccountPanel({
  email,
  onDeleted,
}: {
  email: string | null;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function handleExport() {
    setBusy(true);
    setMsg(null);
    const err = await downloadMyData();
    setBusy(false);
    if (err) setMsg(err.error);
  }

  async function handleDelete() {
    setBusy(true);
    setMsg(null);
    const err = await deleteMyAccount();
    if (err) {
      setBusy(false);
      setMsg(err.error);
      return;
    }
    // Wipe local data too, then reset the app to a fresh anonymous state.
    await deleteDb();
    onDeleted();
  }

  return (
    <div className="account-panel">
      <p className="signed-in-line">
        Signed in as <strong>{email}</strong>. Your inspections are saved to
        this account.
      </p>
      <div className="account-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={handleExport}
          disabled={busy}
        >
          Download my data
        </button>
        {confirming ? (
          <div className="account-confirm" role="alertdialog">
            <p className="account-confirm-text">
              Permanently delete your account and every record? This cannot be
              undone.
            </p>
            <div className="account-confirm-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setConfirming(false)}
                disabled={busy}
              >
                Keep my account
              </button>
              <button
                type="button"
                className="btn-danger"
                onClick={handleDelete}
                disabled={busy}
              >
                {busy ? 'Deleting…' : 'Yes, delete everything'}
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            className="btn-danger-ghost"
            onClick={() => setConfirming(true)}
            disabled={busy}
          >
            Delete my account
          </button>
        )}
      </div>
      {msg && (
        <p role="alert" className="form-error">
          {msg}
        </p>
      )}
    </div>
  );
}
